<template>
	<a
		:href="url"
		tabindex="0"
		:class="[
			'cta',
			{ 'cta--inline': inline },
			'ie-focusable',
			[chevron === 'right' ? 'chevron-right-helper no_hov_under chev' : '']
		]"
		:target="targetBlank === false ? '_self' : '_blank'"
		:rel="targetBlank !== false ? 'noopener noreferrer' : null"
		@click="onClick"
	>
		<v-breakpoint v-if="isAnchorLink" v-model="model" />
		<template v-if="chevron === 'right'">
			<!-- @slot Add custom content between the anchor link and chevron -->
			<slot />
			<span class="cta__text" v-html="text" /><span
				class="icon icon-right"
				aria-hidden="true"
			/>
		</template>
		<template v-else-if="chevron === 'left'">
			<!-- @slot Add custom content after the left chevron icon -->
			<span class="icon icon-left" aria-hidden="true" /><span
				class="cta__text"
				v-html="text"
			/><slot />
		</template>

		<template v-else>
			<!-- @slot Add custom content before the text span -->
			<slot name="left-icon" />
			<span class="cta__text" v-html="text" />
			<!-- @slot Add custom content before the right icon -->
			<slot name="right-icon" />
		</template>
	</a>
</template>

<script>
import { VBreakpoint, Model } from "@vg-faset/core";
let VueScrollTo = require("vue-scrollto");

/**
 * This component makes a CTA.
 */
export default {
	name: "CTA",

	props: {
		/**
		 * The CTA text.
		 */
		text: {
			type: String,
			required: true
		},

		/**
		 * The CTA Url.
		 */
		url: {
			type: String,
			required: true
		},

		/**
		 * This is the chevron icon position in relation to the CTA text/link. There is a slot on the opposite side of the chevron if you need another icon (lock, for example, on PDP). If no value is provided for chevron, there will be a "left-icon" slot on left and a "right-icon" slot on the right.
		 * @values left, right
		 */
		chevron: {
			type: String,
			required: false,
			validator: function (value) {
				return ["left", "right"].indexOf(value) >= 0;
			}
		},

		/**
		 * When true, this can be positioned inline with text.
		 */
		inline: {
			type: Boolean,
			required: false
		},

		/**
		 * This adds `target="_blank" :rel="noopener noreferrer"` when `true`.
		 */
		targetBlank: {
			type: Boolean,
			required: false,
			default: null
		}
	},

	components: { VBreakpoint },

	data() {
		return {
			model: new Model(),
			offset: 0
		};
	},

	computed: {
		isAnchorLink() {
			return this.url.indexOf("#") === 0 && this.url.length > 1;
		}
	},

	methods: {
		getOffset() {
			let inPageNav = document.getElementById("in-page-nav");
			if ((this.model.isMedium || this.model.isLarge) && inPageNav) {
				return inPageNav.scrollHeight * -1 - 50 - this.offset;
			} else {
				return -25 - this.offset;
			}
		},

		onClick(e) {
			if (this.isAnchorLink) {
				e.preventDefault();
				VueScrollTo.scrollTo(this.url, {
					offset: this.getOffset()
				});
			}
		}
	}
};
</script>

<style lang="scss">
.cta {
	color: $color--red;
	display: table;
	font-size: 14px;
	font-weight: $font-weight--bold;
	text-decoration: none;
	transition: 0.2s ease all;

	@include hover {
		color: $color--dark-red;
		text-decoration: underline;
	}

	&:not(:first-child) {
		margin-top: 22px;
	}

	&--inline {
		display: inline;
		font-size: inherit;
		margin: 0;
		text-decoration: underline;
		white-space: nowrap;
	}

	&.chevron-right-helper.no_hov_under.chev {
		&:hover {
			text-decoration: none;
			span {
				&.cta__text {
					text-decoration: underline;
				}
			}
		}
	}

	.icon {
		display: inline-block;
		font-size: 12px;
		margin-left: 2px;
		vertical-align: middle;

		&:first-child:not(.cta__text):not(.icon-left) {
			margin-left: 0;
			margin-right: 6px;
		}

		&:last-child:not(.cta__text):not(.icon-right) {
			margin-left: 6px;
			margin-right: 0;
		}

		&.icon-right {
			display: inline;
			padding-right: 4px;
			&:before {
				position: relative;
				left: 0px; // transform doesn't work on inline element
				transition: left 0.25s ease 0s;
			}
		}
	}

	.icon-down,
	.icon-up {
		height: 8px;
		line-height: 8px;
		margin-left: 8px;
		vertical-align: top;
	}

	.icon-lock {
		font-size: 18px;
		margin-right: 2px;
		vertical-align: text-bottom;
	}

	.icon-left,
	.icon-right {
		transition: transform 0.25s ease;
	}

	&:hover,
	&:focus {
		.icon-left {
			transform: translateX(-4px);
		}

		.icon-right {
			// transform: translateX(4px);
			text-decoration: none;
			&:before {
				left: 4px; // transform doesn't work on inline element
			}
		}
	}

	.icon-left::before {
		margin-right: 4px;
	}

	.icon-right::before {
		margin-left: 4px;
	}
}
</style>
